// Contexte d'une instance Feedbaacks : client, libellés, réglages, état UI,
// temps réel, notifications. Tout composant lit d'ici, rien n'est global :
// deux widgets sur une page restent indépendants.
import { createContext, type ComponentChildren, type JSX } from 'preact'
import { useContext, useEffect, useMemo, useState } from 'preact/hooks'
import type { FeedbaacksClient, FeedbackItem, MemberRole, RealtimeEvent, ArchivedView, FeedbackType, Sort } from '@feedbaacks/sdk'
import { createI18n, type Locale, type Messages, type Translate } from './i18n'
import { createEmitter, createStore, type Emitter, type Store } from './store'
import { installConsoleBuffer } from './capture'

export type StatusGroup = 'all' | 'active' | 'open' | 'in_progress' | 'on_hold' | 'shipped' | 'declined'

export interface Filters {
  type: FeedbackType | 'all'
  statusGroup: StatusGroup
  mine: boolean
  others: boolean
  unread: boolean
  search: string
  sort: Sort
  archived: ArchivedView
  trash: boolean
  authorId: string | null
}

export const DEFAULT_FILTERS: Filters = {
  type: 'all', statusGroup: 'active', mine: false, others: false, unread: false, search: '',
  sort: 'score', archived: 'active', trash: false, authorId: null,
}

export interface UiState {
  open: boolean
  composerOpen: boolean
  filtersOpen: boolean
  selectedId: string | null
  /** Commentaire ciblé par un lien profond (surligné, amené à l'écran). */
  highlightCommentId: string | null
  /** Commentaire auquel on est en train de répondre. */
  replyToId: string | null
  editingId: string | null
  previewId: string | null
  pendingType: FeedbackType | null
  draft: { type: FeedbackType; title: string; body: string } | null
  filters: Filters
  knownAuthors: Array<{ id: string; name: string }>
}

const initialState: UiState = {
  open: false, composerOpen: false, filtersOpen: false, selectedId: null, highlightCommentId: null, replyToId: null, editingId: null, previewId: null,
  pendingType: null, draft: null, filters: DEFAULT_FILTERS, knownAuthors: [],
}

/** Rendus surchargeables : chaque slot reçoit l'élément et rend ce qu'il veut. */
export interface Slots {
  /** Sous le titre d'une ligne (méta). */
  rowMeta?: (item: FeedbackItem) => ComponentChildren
  /** Dans le détail, avant la discussion (ex. lecteur vidéo calé au temps). */
  detail?: (item: FeedbackItem) => ComponentChildren
  /** Dans le formulaire, sous la description (champs propres à l'app). */
  composer?: (api: { setContext: (patch: Record<string, unknown>) => void }) => ComponentChildren
  /** Liste vide, premier passage. */
  empty?: () => ComponentChildren
}

export type UiEvent =
  | { type: 'created'; item: FeedbackItem }
  | { type: 'updated'; item: FeedbackItem }
  | { type: 'deleted'; id: string }
  | { type: 'open' }
  | { type: 'close' }

export interface CaptureOptions {
  /** Page, viewport, navigateur au dépôt (défaut : oui). */
  page?: boolean
  /** Erreurs console récentes, masquées, jointes aux bugs (défaut : oui). */
  console?: boolean
  /** Capture d'écran : 'bug' = jointe en silence aux bugs (comportement Faast),
   *  'manual' = bouton pour l'équipe seulement, 'off'. Défaut : 'bug'. */
  screenshot?: 'bug' | 'manual' | 'off'
  /** Capture fournie par l'hôte (ex. l'image courante d'un lecteur vidéo) ;
   *  par défaut, la page sans l'interface Feedbaacks. */
  takeScreenshot?: () => Promise<Blob>
}

export interface UiOptions {
  client: FeedbaacksClient
  locale?: Locale
  /** Libellés surchargés (clés de Messages). */
  labels?: Partial<Messages>
  /** Contexte métier joint à chaque dépôt (ex. Academy : capsule, temps). */
  context?: () => Record<string, unknown> | Promise<Record<string, unknown>>
  /** Libellé court du contexte d'un retour (ligne). Défaut : context.label. */
  contextLabel?: (item: FeedbackItem) => string | null
  capture?: CaptureOptions
  slots?: Slots
  /** Libellés des canaux d'origine (source) affichés en pastille. */
  sourceLabels?: Record<string, string>
  /** Lien profond vers un retour (tableau de suivi). */
  linkTo?: (item: FeedbackItem) => string
  /** Rôle d'équipe connu de l'hôte (tableau). Sinon : celui de la session. */
  role?: MemberRole | ''
  /** Identifiant Feedbaacks de la personne connectée, si l'hôte le connaît.
   *  Sinon : lu dans la session (widget) ou par /api/me. */
  userId?: string
  /** Temps réel (défaut : oui). */
  realtime?: boolean
  /** Raccourci ⌘⇧F / Ctrl+Maj+F et N (défaut : oui). */
  shortcut?: boolean
  /** Bulle de coin (défaut : oui). Sans elle, l'hôte ouvre par son propre
   *  bouton (Feedbaacks.open()). */
  trigger?: boolean
  onEvent?: (e: UiEvent) => void
}

export interface Toast {
  id: number
  kind: 'error' | 'success' | 'info'
  message: string
}

export interface ConfirmRequest {
  title: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
  destructive?: boolean
  resolve: (ok: boolean) => void
}

export interface Instance {
  client: FeedbaacksClient
  options: UiOptions
  t: Translate
  locale: Locale
  store: Store<UiState>
  role: MemberRole | ''
  /** La personne connectée ('' tant qu'elle n'est pas connue). */
  userId: string
  /** Événements temps réel du serveur. */
  realtime: Emitter<RealtimeEvent>
  /** Retour modifié localement (liste et détail restent alignés). */
  changes: Emitter<{ item?: FeedbackItem; removedId?: string }>
  notify: (kind: Toast['kind'], message: string) => void
  confirm: (req: Omit<ConfirmRequest, 'resolve'>) => Promise<boolean>
  toasts: Store<{ list: Toast[]; confirm: ConfirmRequest | null }>
}

const Ctx = createContext<Instance | null>(null)

export function useFeedbaacks(): Instance {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useFeedbaacks : composant hors de <FeedbaacksProvider>')
  return ctx
}

let toastSeq = 0

/** Crée une instance (utilisable hors Preact : widget, tests). */
export function createInstance(options: UiOptions): Instance {
  const locale = options.locale ?? 'fr'
  const toasts = createStore<{ list: Toast[]; confirm: ConfirmRequest | null }>({ list: [], confirm: null })
  return {
    client: options.client,
    options,
    t: createI18n(locale, options.labels),
    locale,
    store: createStore(initialState),
    role: options.role ?? '',
    userId: options.userId ?? '',
    realtime: createEmitter<RealtimeEvent>(),
    changes: createEmitter(),
    toasts,
    notify: (kind, message) => {
      const id = ++toastSeq
      toasts.set((s) => ({ list: [...s.list, { id, kind, message }] }))
      setTimeout(() => toasts.set((s) => ({ list: s.list.filter((x) => x.id !== id) })), 5000)
    },
    confirm: (req) => new Promise<boolean>((resolve) => toasts.set({ confirm: { ...req, resolve } })),
  }
}

export function FeedbaacksProvider({ instance, children }: { instance: Instance; children: ComponentChildren }): JSX.Element {
  const [role, setRole] = useState(instance.role)
  const [userId, setUserId] = useState(instance.userId)
  // Rôle et personne : fournis par l'hôte (tableau), lus dans la session
  // (widget), ou demandés à /api/me (clé serveur).
  useEffect(() => {
    const known = instance.options.role !== undefined
    if (instance.client.authType === 'session') {
      if (known && instance.userId) return
      instance.client.currentSession().then((s) => {
        if (!known) setRole(s.role)
        setUserId(s.user.id)
      }, () => {})
    } else if (!instance.userId) {
      Promise.resolve().then(() => instance.client.account.me()).then((m) => setUserId(m.user.id), () => {})
    }
  }, [instance])
  useEffect(() => (instance.options.capture?.console === false ? undefined : installConsoleBuffer()), [instance])
  useEffect(() => {
    if (instance.options.realtime === false) return
    return instance.client.subscribe((e) => instance.realtime.emit(e))
  }, [instance])
  const value = useMemo(() => ({ ...instance, role, userId }), [instance, role, userId])
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}
